import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/Alert';
import { EmailDeliveryGuidance } from '../../components/EmailDeliveryGuidance';
import { authApi } from '../../features/auth/auth.api';
import { toDisplayMessage } from '../../lib/error-message';
import { paths } from '../../routes/paths';

import styles from './AuthForm.module.css';
import { AuthFormField } from './AuthFormField';
import { AuthLayout } from './AuthLayout';

export function ForgotPasswordPage(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      await authApi.forgotPassword(email);
      setSubmitted(true);
    } catch (caught: unknown) {
      setError(toDisplayMessage(caught, 'Unable to send the email. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout
        title="Check your email"
        description="If an account exists for this email address, we've sent password reset instructions."
        footer={
          <p>
            <Link to={paths.login}>Back to sign in</Link>
          </p>
        }
      >
        <Alert tone="success">
          If an account exists for this email address, we&apos;ve sent password reset instructions.
        </Alert>
        <EmailDeliveryGuidance />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot your password?"
      description="Enter the email address associated with your account and we'll send you a reset link."
      heroHeadline="Secure access to your Seamontech account."
      heroLead="We'll help you get back to managing your solar journey."
      footer={
        <p>
          Remember your password? <Link to={paths.login}>Sign in</Link>
        </p>
      }
    >
      <form className={styles.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error !== null && <Alert tone="error">{error}</Alert>}

        <AuthFormField
          label="Email address"
          inputType="email"
          name="email"
          icon="mail"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          autoComplete="email"
          placeholder="you@example.com"
          required
        />

        <button className={styles.submitButton} type="submit" disabled={busy} aria-busy={busy}>
          {busy ? 'Sending…' : 'Send reset link'}
          {!busy && (
            <span className={styles.submitArrow} aria-hidden="true">
              →
            </span>
          )}
        </button>
      </form>
    </AuthLayout>
  );
}
